import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import CustomLoader from "../../../components/CustomLoader";
import { shallow } from "zustand/shallow";
import { useStoreWrapper } from "../../../store/store";
import ApprovalList from "./component/approval-list";
import Modal from "../../../components/Modal";
import { AiOutlineClose } from "react-icons/ai";
import moment from "moment";
import {
  OutlinedButton,
  FilledButton,
} from "../../../components/Forms/Button/buttons.style";
import { Container } from "../../User/Users/components/logIncident/logIncident.style";
import useIncident from "../../../hooks/useIncident";
import {
  getFacilityRole,
  hasUserSigned,
  isSignatoryApprovable,
} from "../../../utils/formGenUtils/approvalUtils";
import { toast } from "react-toastify";

import {
  ApprovalHeading,
  ApprovalSectionHeading,
  Divider,
  IncidentNumber,
  SubApprovalHeading,
  SubmittedBy,
} from "./component/approval-list/style";
import {
  IncidentSummaryGrid,
  IncidentSummaryItem,
  IncidentSummaryLabel,
  IncidentSummaryValue,
} from "../../../components/Forms/incidentForm/styles";
import { formatDisplayDateTime } from "../../../components/Forms/dateTimeInput/methods";
import { PLACEHOLDER_VALUE, PERMISSION_LIST } from "../../../shared/sharedConstants";
import { useLocation, useNavigate, useParams, useBlocker } from "react-router-dom";
import { usePermission } from "../../../hooks/usePermission";
import { stages } from "../../../utils/helpers/incidentFormTabs";
import {
  IncidentStatus,
  TabOptions,
} from "../../../types/fieldInstance.interface";
import AlertDialog from "../../User/Users/components/AlertDialog/AlertDialog";
import CreateOrUpdateSignatory from "./component/ManageApproval";
import MarkReadyConfirmModal from "../../../components/MarkReadyConfirmModal/MarkReadyConfirmModal";
import { markIncidentReadyForApproval } from "../../../api/incident/apiIncidents";
import { queryClient } from "../../../App";
import ToggleSwitch from "../../../components/Forms/ToggleSwitch";

interface SignatoryData {
  incidentSignatureId: string;
  signedById: string;
  signedByName: string;
}

export enum ActionType {
  Approve = "Approve",
  Edit = "Edit",
  Reject = "Reject",
  Reset = "Reset",
}

const Approval = () => {
  const hasRefetched = useRef(false);
  const [openModal, setOpenModal] = useState(false);
  const [action, setAction] = useState<ActionType | "">("");
  const [shouldShowLoader, setShouldShowLoader] = useState(false);
  const [incidentSignatureId, setIncidentSignatureId] = useState("");
  const [isConclusionFilled, setIsConclusionFilled] = useState(false);
  const [comment, setComment] = useState("");
  
  // Mark Ready modal state
  const [showMarkReadyModal, setShowMarkReadyModal] = useState(false);
  const [markReadyLoading, setMarkReadyLoading] = useState(false);
  const [markReadyToggle, setMarkReadyToggle] = useState(false);
  const hasUserSignedInSession = useRef(false);
  const hasShownMarkReadyModal = useRef(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { isPermitted } = usePermission();
  const { forms, user, userFacilities, resetFormState } = useStoreWrapper(
    (state) => ({
      forms: state.forms,
      user: state.user,
      userFacilities: state.userFacilities,
      resetFormState: state.resetForm,
    }),
    shallow
  );
  const { incidentIdOrNew: incidentID } = useParams();
  const incidentId = incidentID || "";
  const formDetails = forms[incidentId];

  const {
    getIncidentSignatories,
    addSignatory: {
      mutateAsync: appendSignatureAsync,
      isSuccess: appendSignatureSuccess,
      isLoading: appendSignatureLoading,
    },
    resetSignatory: {
      mutateAsync: resetSignatureAsync,
      isSuccess: resetSignatureSuccess,
      isLoading: resetSignatureLoading,
    },
    getIncident: {
      data: incidentData,
      isSuccess: incidentDataSuccess,
      isLoading: incidentDataLoading,
      refetch: refetchIncident,
      isFetching: isIncidentFetching,
    },
  } = useIncident({ incidentId });

  const {
    isLoading: signatoriesLoading,
    data: signatoriesData,
    refetch,
  } = getIncidentSignatories;
  const signatories = signatoriesLoading ? [] : signatoriesData?.data.data;

  const incidentDetails =
    incidentDataLoading || !incidentDataSuccess
      ? null
      : incidentData?.data?.data;
  const [openConclusionsModal, setOpenConclusionsModal] = useState(false);

  useEffect(() => {
    setShouldShowLoader(
      incidentDataLoading || signatoriesLoading || isIncidentFetching
    );
  }, [incidentDataLoading, signatoriesLoading, isIncidentFetching]);

  useEffect(() => {
    let conclusionFilled = !!incidentDetails?.Conclusion__c;
    if (incidentDetails && !conclusionFilled) {
      if (!hasRefetched.current) {
        hasRefetched.current = true;
        refetchIncident().then((result: any) => {
          const incident =
            incidentDataLoading || !incidentDataSuccess
              ? null
              : result?.data?.data?.data;
          conclusionFilled = !!incident?.Conclusion__c;
          if (incidentDetails && !conclusionFilled) {
            setOpenConclusionsModal(true);
          }
        });
      }
    }
    setIsConclusionFilled(conclusionFilled);
  }, [incidentDetails]);

  useEffect(() => {
    if (appendSignatureSuccess || resetSignatureSuccess) {
      refetch();
      refetchIncident(); // Refresh incident to get updated status (Submitted -> Signature in Progress)
      // MARCC-776: refresh the incident form's cached copy too, so the Submit/Edit buttons
      // re-evaluate the new status and hide in place after signing (no navigation needed).
      queryClient.invalidateQueries(["incident", incidentId]);
    }
  }, [
    appendSignatureSuccess,
    resetSignatureSuccess,
    incidentId,
    refetch,
    refetchIncident,
  ]);

  const directorSignOff = signatories.filter(
    (signatory: any) =>
      !signatory.Facility_Signature_Requirement__r.Director_SignOff__c
  );
  const qapiSignOff = signatories.filter(
    (signatory: any) =>
      signatory.Facility_Signature_Requirement__r.Director_SignOff__c
  );
  const userRole = getFacilityRole(
    userFacilities,
    incidentDetails?.Facility__c
  );

  const userSignedOff = hasUserSigned(signatories, userRole?.assignmentId);
  function hasLevelSigned(levelData: any[]) {
    return levelData.every(
      (signatory: { Contact_on_Facility__c: string }) =>
        !!signatory?.Contact_on_Facility__c
    );
  }
  const hasFirstLevelSigned = hasLevelSigned(directorSignOff ?? []);
  const isQAPIDisabled = !hasFirstLevelSigned;
  const hasSecondLevelSigned = hasLevelSigned(qapiSignOff ?? []);

  const validStatusesForApproval = [
    IncidentStatus.SignatoryInProgress,
    IncidentStatus.Submitted,
    IncidentStatus.ReadyForApproval,
  ];

  const isStatusValid =
    !!incidentDetails &&
    validStatusesForApproval.includes(incidentDetails.Incident_Status__c);

  const isActionLoading = appendSignatureLoading || resetSignatureLoading;

  const approvableSignatoryId = useMemo(() => {
    const baseOpts = {
      userSignedOff,
      isActionLoading,
      isConclusionFilled: !!isConclusionFilled,
      isStatusValid,
    };

    const directorSignatory = directorSignOff.find((signatory: any) =>
      isSignatoryApprovable(signatory, userRole?.roleName, baseOpts)
    );
    if (directorSignatory?.Id) return directorSignatory.Id;

    const qapiSignatory = qapiSignOff.find((signatory: any) =>
      isSignatoryApprovable(signatory, userRole?.roleName, {
        ...baseOpts,
        isQAPILevel: true,
        isQAPIDisabled,
      })
    );
    if (qapiSignatory?.Id) return qapiSignatory.Id;

    return "";
  }, [
    directorSignOff,
    isActionLoading,
    isConclusionFilled,
    isQAPIDisabled,
    isStatusValid,
    qapiSignOff,
    userRole?.roleName,
    userSignedOff,
  ]);

  // Keep modal signer options in sync with the existing Approve button rules.
  const canUserSignOff = !!approvableSignatoryId;

  // Determine if incident is in Submitted status (for Mark Ready modal logic)
  const isSubmittedStatus = incidentDetails?.Incident_Status__c === IncidentStatus.Submitted;

  // Check if user has permission to edit the incident post-submission
  const canEditIncident = isPermitted(
    [PERMISSION_LIST.EDIT_OWN_INCIDENT_POST_SUBMISSION, PERMISSION_LIST.EDIT_ALL_INCIDENTS_POST_SUBMISSION],
    incidentDetails?.Facility__c
  );

  // Determine if we should block navigation (user CAN sign off but HASN'T, and hasn't seen modal)
  // Sign-off capability should drive this modal path, not edit permission.
  const shouldBlockNavigation = 
    isSubmittedStatus &&
    isConclusionFilled &&
    canUserSignOff &&
    !hasUserSignedInSession.current &&
    !hasShownMarkReadyModal.current;

  // Use React Router's useBlocker to intercept navigation
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      shouldBlockNavigation && currentLocation.pathname !== nextLocation.pathname
  );

  // Handle sign-off (approve) from the new modal by opening the existing comment modal flow.
  function handleApproveSignOff() {
    const targetIncidentSignatureId = approvableSignatoryId || incidentSignatureId;
    if (!targetIncidentSignatureId) {
      toast.error("No eligible signatory found for approval", {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 2000,
        toastId: "sign-off-error-no-signatory",
      });
      return;
    }

    setShowMarkReadyModal(false);
    setAction(ActionType.Approve);
    setComment("");
    setIncidentSignatureId(targetIncidentSignatureId);
    setOpenModal(true);
  }

  // Handle Mark Ready confirmation - supports both immediate modal and blocker-triggered modal
  async function handleConfirmMarkReady() {
    setMarkReadyLoading(true);
    try {
      await markIncidentReadyForApproval(incidentId);
      toast.success("Incident marked as Ready for Approval", {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 2000,
        toastId: "mark-ready-success",
      });
      await Promise.all([
        queryClient.invalidateQueries(["incidentsWithParams"]),
        queryClient.invalidateQueries(["incident", incidentId]),
        queryClient.invalidateQueries(["incidentDetailsWithId"]),
      ]);
      setShowMarkReadyModal(false);
      hasShownMarkReadyModal.current = true;
      if (blocker.state === "blocked") {
        blocker.proceed();
      }
    } catch (err: any) {
      console.error("Error marking incident ready:", err);
      toast.error(err?.response?.data?.message || "Failed to mark incident as Ready for Approval", {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 2000,
        toastId: "mark-ready-error",
      });
    } finally {
      setMarkReadyLoading(false);
    }
  }

  function handleCloseMarkReadyModal() {
    setShowMarkReadyModal(false);
    setMarkReadyToggle(false);
    // If navigation was blocked, set flag and proceed so the blocker doesn't keep triggering
    if (blocker.state === "blocked") {
      hasShownMarkReadyModal.current = true;
      blocker.proceed();
    }
  }

  function handleMarkReadyToggleChange() {
    if (!markReadyToggle) {
      // Only show modal when toggling ON
      setShowMarkReadyModal(true);
    }
    setMarkReadyToggle(!markReadyToggle);
  }

  // Determine if we should show the mark ready toggle
  // Only show when status is Submitted - toggle disappears when status changes to ReadyForApproval
  const shouldShowMarkReadyToggle = 
    !shouldShowLoader &&
    incidentDetails?.Incident_Status__c === IncidentStatus.Submitted &&
    isConclusionFilled &&
    signatories?.length > 0 &&
    !canUserSignOff &&
    canEditIncident;

  // Removed immediate modal show for users without sign-off permissions
  // Now they get a toggle instead (see handleMarkReadyToggleChange)

  // Track when user successfully signs off
  useEffect(() => {
    if (appendSignatureSuccess && action === ActionType.Approve) {
      hasUserSignedInSession.current = true;
    }
  }, [appendSignatureSuccess, action]);

  // Reset markReadyToggle when status changes away from Submitted
  // Keeps internal state clean even when toggle is hidden
  useEffect(() => {
    if (
      incidentDetails?.Incident_Status__c !== IncidentStatus.Submitted &&
      markReadyToggle
    ) {
      setMarkReadyToggle(false);
    }
  }, [incidentDetails?.Incident_Status__c, markReadyToggle]);

  // When blocker is triggered, show the Mark Ready modal
  useEffect(() => {
    if (blocker.state === "blocked") {
      setShowMarkReadyModal(true);
    }
  }, [blocker.state]);

  // Block browser close/reload if user hasn't signed and hasn't seen modal
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (shouldBlockNavigation) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [shouldBlockNavigation]);

  useEffect(() => {
    return () => {
      if (formDetails?.formState?.shouldResetForm) {
        resetFormState(incidentId);
      }
    };
  }, [formDetails?.formState?.shouldResetForm]);

  function closeModal() {
    setAction("");
    setIncidentSignatureId("");
    setOpenModal(false);
  }

  function navigateToConclusions() {
    setOpenConclusionsModal(false);
    const conclusionStage = stages.find(
      (stage) => stage.label === TabOptions.Conclusion
    );
    const currentPath = location.pathname;
    const pathSegments = currentPath.split("/");
    pathSegments.pop();
    if (conclusionStage) {
      pathSegments.push(conclusionStage.path);
      const newPath = `${pathSegments.join("/")}${"?mode=edit"}`;
      setOpenConclusionsModal(false);
      navigate(newPath, { state: { from: currentPath } });
    }
  }

  function isFinalSignOff(signatoryId: string): boolean {
    const otherSignatories = signatories.filter(
      (signatory: { Id: string }) => signatory.Id !== signatoryId
    );
    return otherSignatories.every(
      (signatory: { Contact_on_Facility: string | undefined }) =>
        !!signatory.Contact_on_Facility
    );
  }

  function getApprovalToastMessage() {
    if (action === ActionType.Edit) {
      return "Comment Successfully Updated";
    }
    if (isFinalSignOff(signatoryData.incidentSignatureId)) {
      return "Incident Sign off Successful";
    } else {
      return "Incident Successfully Approved";
    }
  }

  function resetSignatory() {
    resetSignatureAsync(incidentSignatureId, {
      onSuccess: () => {
        toast.success("Signature reset successfully", {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 2000,
          toastId: "2",
        });
        closeModal();
      },
      onError: () => {
        toast.error("Failed to reset signature", {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 2000,
          toastId: "3",
        });
      },
    });
  }

  function onActionButtonClick(signatoryData: SignatoryData) {
    action !== ActionType.Reset
      ? appendSignatureAsync(signatoryData, {
          onSuccess: () => {
            if (action === ActionType.Approve || action === ActionType.Edit) {
              const toastMessage = getApprovalToastMessage();
              toast.success(toastMessage, {
                position: toast.POSITION.TOP_RIGHT,
                autoClose: 2000,
                toastId: "1",
              });
              if (action === ActionType.Approve) {
                hasUserSignedInSession.current = true;
                hasShownMarkReadyModal.current = true;
                if (blocker.state === "blocked") {
                  blocker.proceed();
                }
              }
              closeModal();
            } else if (action === ActionType.Reject) {
              toast.warn("Incident Rejected", {
                position: toast.POSITION.TOP_RIGHT,
                autoClose: 2000,
                toastId: "1",
              });
              closeModal();
            }
          },
          onError: () => {
            toast.error("Something went wrong", {
              // use server errors instead
              position: toast.POSITION.TOP_RIGHT,
              autoClose: 2000,
              toastId: "1",
            });
          },
        })
      : resetSignatory();
  }

  const handleCommentsChange = (event: { target: { value: any } }) => {
    setComment(event.target.value);
  };

  function getStatus(action: ActionType | ""): string {
    switch (action) {
      case ActionType.Approve:
      case ActionType.Edit:
        return "Approved";
      case ActionType.Reject:
        return "Rejected";
      default:
        return "";
    }
  }

  function getAction(action: ActionType | ""): string {
    switch (action) {
      case ActionType.Approve:
        return "Approving";
      case ActionType.Reject:
        return "Rejecting";
      case ActionType.Reset:
        return "Resetting";
      case ActionType.Edit:
        return "Updating";
      default:
        return "";
    }
  }

  const signatoryData = {
    incidentSignatureId,
    signedById: userRole?.assignmentId,
    signedByName: `${user.firstName} ${user.lastName}`,
    signatureComment: comment,
    signatureStatus: getStatus(action),
  };

  return (
    <>
      {!shouldShowLoader ? (
        <div className="h-full">
          <ApprovalHeading>
            <SubApprovalHeading>
              Approvals
              <SubmittedBy>
                Submitted by: {incidentDetails?.Incident_Created_By_Name__c}{" "}
                <Divider>|</Divider>
                {moment(incidentDetails?.CreatedDate).format(
                  "DD/MMM/YYYY"
                )}{" "}
                <Divider>|</Divider>
                {moment(incidentDetails?.CreatedDate).local().format("kk:mm")}
              </SubmittedBy>
            </SubApprovalHeading>
            {!!directorSignOff.length && (
              <IncidentNumber>
                Incident Number: {incidentDetails?.Incident_Name__c}
                {incidentDetails.Incident_Risk_Rating__c &&
                  incidentDetails.Incident_Score__c && (
                    <div style={{ color: "#8b6697" }} className="text-sm">
                      <span>
                        Risk Rating: {incidentDetails?.Incident_Risk_Rating__c}
                      </span>
                      <span className="ml-5">
                        Score: {incidentDetails?.Incident_Score__c}
                      </span>
                    </div>
                  )}
              </IncidentNumber>
            )}
          </ApprovalHeading>
          
          {/* Incident summary: Resident Name / Date-Time / Status */}
          {(() => {
            const residentName =
              incidentDetails?.Resident_Name__c || PLACEHOLDER_VALUE;
            const rawIncidentDateTime =
              incidentDetails?.Incident_Date_Time__c || null;
            const formattedIncidentDateTime = formatDisplayDateTime(
              rawIncidentDateTime
            );
            const statusDisplay =
              incidentDetails?.Incident_Status__c || PLACEHOLDER_VALUE;

            const incidentSummaryItems = [
              { label: "Resident Name", value: residentName },
              { label: "Date/Time of Incident", value: formattedIncidentDateTime },
              { label: "Status", value: statusDisplay },
            ];

            const shouldShowIncidentSummary = incidentSummaryItems.some(
              (item) => item.value && item.value !== PLACEHOLDER_VALUE
            );

            return shouldShowIncidentSummary ? (
              <IncidentSummaryGrid data-testid="incident-summary-grid">
                {incidentSummaryItems.map(({ label, value }) => (
                  <IncidentSummaryItem key={label}>
                    <IncidentSummaryLabel>{label}</IncidentSummaryLabel>
                    <IncidentSummaryValue>{value}</IncidentSummaryValue>
                  </IncidentSummaryItem>
                ))}
              </IncidentSummaryGrid>
            ) : null;
          })()}
          {!signatories.length && (
            <div
              style={{ color: "#8b6697", height: "50%" }}
              className="mx-auto mt-14 flex items-center text-center text-lg md:w-1/3"
            >
              Signatory requirements not defined for this facility. Please,
              contact Admin.
            </div>
          )}

          {!!directorSignOff.length && (
            <>
              <div className="mb-10 mt-14 flex items-center justify-between">
                <ApprovalSectionHeading className="text-2xl">
                  Clinical Sign-off
                </ApprovalSectionHeading>
                {shouldShowMarkReadyToggle && (
                  <div className="flex items-center">
                    <ToggleSwitch
                      name="markReadyToggle"
                      label="Mark Ready for Approval"
                      value={markReadyToggle}
                      onChange={handleMarkReadyToggleChange}
                      disabled={markReadyLoading}
                    />
                  </div>
                )}
              </div>
              <ApprovalList
                data={directorSignOff}
                handleClick={() => setOpenModal(true)}
                userRole={userRole.roleName}
                userId={userRole?.assignmentId}
                setComment={setComment}
                hasUserSigned={userSignedOff}
                hasSecondLevelSigned={hasSecondLevelSigned}
                setAction={setAction}
                setIncidentSignatureId={setIncidentSignatureId}
                isActionLoading={isActionLoading}
                isConclusionFilled={isConclusionFilled}
                isStatusValid={isStatusValid}
              />
            </>
          )}
          {!!qapiSignOff.length && (
            <>
              <ApprovalSectionHeading>
                Administrative Sign-off
              </ApprovalSectionHeading>
              <ApprovalList
                data={qapiSignOff}
                handleClick={() => setOpenModal(true)}
                userRole={userRole.roleName}
                userId={userRole?.assignmentId}
                setComment={setComment}
                hasUserSigned={userSignedOff}
                isQAPIDisabled={isQAPIDisabled}
                setAction={setAction}
                setIncidentSignatureId={setIncidentSignatureId}
                isActionLoading={isActionLoading}
                isStatusValid={isStatusValid}
                isConclusionFilled={isConclusionFilled}
              />
            </>
          )}

          <Modal open={openModal}>
            <Container>
              <div className="flex h-full flex-col justify-between">
                <AiOutlineClose
                  onClick={closeModal}
                  className="cursor-pointer"
                  size={23}
                />

                <div>
                  <CreateOrUpdateSignatory
                    action={action}
                    handleCommentsChange={handleCommentsChange}
                    comment={comment}
                  />
                </div>

                <div className="flex justify-end">
                  <OutlinedButton
                    onClick={closeModal}
                    //@ts-ignore
                    className="mr-3"
                  >
                    Cancel
                  </OutlinedButton>
                  <FilledButton
                    onClick={() => onActionButtonClick(signatoryData)}
                    data-testid={`approve-btn-${userRole.roleId}`}
                    disabled={isActionLoading}
                  >
                    {isActionLoading ? getAction(action) : action}
                  </FilledButton>
                </div>
              </div>
            </Container>
          </Modal>
          {openConclusionsModal && (
            <AlertDialog
              message="Please fill out the conclusion section to proceed with the signatory process."
              onCancel={() => setOpenConclusionsModal(false)}
              onConfirm={navigateToConclusions}
              actionButtonText="Conclusions"
            />
          )}
          <MarkReadyConfirmModal
            open={showMarkReadyModal}
            onClose={handleCloseMarkReadyModal}
            onApprove={handleApproveSignOff}
            onMarkReady={handleConfirmMarkReady}
            confirmLoading={markReadyLoading}
            canUserSignOff={canUserSignOff}
          />
        </div>
      ) : (
        <div>
          <CustomLoader fullscreen />
        </div>
      )}
    </>
  );
};

export default Approval;
